import { useMemo } from 'react';
import { VIEW_KEY } from '@vagusx/clients';
import { usePersistentViewFilters } from '@/hooks/use-persistent-view-filters';
import {
  hasAnyFilterParam,
  tasksInboxPreferenceToUrl,
  urlToTasksInboxPreference,
} from './task-filter-utils';

interface Args {
  /** Raw URL search params read from the router. */
  search: Record<string, string | undefined>;
  /** Currently selected task id, if any — determines the redirect target route. */
  selectedTaskId: string | undefined;
  /** The router's navigate function. Typed as any to avoid importing router types. */
  navigate: (args: {
    to: string;
    params?: Record<string, string>;
    search?: Record<string, string>;
    replace?: boolean;
  }) => void;
  /**
   * When false, the hook is inert — neither hydrates from saved prefs
   * nor saves on URL changes. The dashboard drilldown sets this to
   * `false` (via `from=dashboard`) so the user's saved tasks-inbox
   * view isn't trampled by transient drilldown filters.
   */
  enabled?: boolean;
}

/**
 * Tasks-specific binding of `usePersistentViewFilters`. Carries the
 * page-specific concerns (which URL primitives matter, whether to land
 * on `/tasks` or `/tasks/$taskId`, preserving the ephemeral search
 * term across the hydrate redirect). The generic mechanic — load,
 * redirect-once, debounce-save — lives in the shared hook.
 */
export function usePersistentInboxFilters({
  search,
  selectedTaskId,
  navigate,
  enabled = true,
}: Args): {
  waitingForHydrate: boolean;
} {
  // Memoize on the persistable URL primitives so the snapshot reference
  // is stable across renders that change other (non-persistable) keys.
  const urlSnapshot = useMemo(
    () => urlToTasksInboxPreference(search),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read primitives, not the whole object
    [search.status, search.show, search.labels, search.dateFrom, search.dateTo]
  );

  return usePersistentViewFilters({
    viewKey: VIEW_KEY.TASKS_INBOX,
    enabled,
    urlSnapshot,
    urlIsBare: !hasAnyFilterParam(search),
    preferenceToUrl: tasksInboxPreferenceToUrl,
    onHydrateRedirect: (target) => {
      // Preserve the ephemeral search term that wasn't part of the
      // saved blob.
      if (search.search) target.search = search.search;
      if (selectedTaskId) {
        navigate({
          to: '/tasks/$taskId',
          params: { taskId: selectedTaskId },
          search: target,
          replace: true,
        });
      } else {
        navigate({ to: '/tasks', search: target, replace: true });
      }
    },
  });
}
